import React, { useEffect, useState } from 'react';

import {
    GetPlayCountAggregationTypesInput,
    GetVendorShareCalculationTypesInput,
    PlayCountAggregationType,
    PlayCountAggregationTypes,
    PlayCountAggregationTypesEnum,
    VendorShareCalculationType,
    VendorShareCalculationTypes,
    VendorShareCalculationTypesEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';

import { cloneDeep, indexOf, isEmpty, isUndefined, sortBy } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { ContractTermInfoButton } from 'src/components/ContractTermInfoButton';
import { Input } from 'src/components/Input';
import { Toggle } from 'src/components/Toggle';
import { ContractTermName } from 'src/constants/contractTermHelpInfo';
import { IS_REQUIRED, MUST_N_SELECTIONS } from 'src/constants/errorMessages';
import { AppContext } from 'src/contexts/appContext';
import { IContractTermsComponentProps } from 'src/types/IRateCard';
import { hasErrorMessage } from 'src/util/validationsUtil';

export const getStrategyTypeDisplayName = (strategyType: string): string => {
    switch (strategyType) {
        case VendorShareCalculationTypesEnum.STANDARD:
        case PlayCountAggregationTypesEnum.STANDARD:
            return 'Standard';
        case VendorShareCalculationTypesEnum.ANTI_DILUTION:
            return 'Anti-dilution';
        case VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_STANDARD:
        case PlayCountAggregationTypesEnum.ARTIST_CENTRIC_2025:
            return 'Artist centric 2025';
        case VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_ANTI_DILUTION:
            return 'Artist centric 2025 & Anti-dilution';
        default:
            return '';
    }
};

export const VendorSharePlayCountCalcStrategy = ({
    contractTerms,
    handleContractTermValueChange,
    setIsContractTermInputValid,
    isEditMode,
    handleContractTermErrorMessageChange,
    contractTermErrorMessages
}: IContractTermsComponentProps) => {
    const { setTopAlertMsg } = React.useContext(AppContext);
    const [allVendorShareCalculationTypes, setAllVendorShareCalculationTypes] = useState<VendorShareCalculationTypes>(
        []
    );
    const [allPlayCountAggregationTypes, setAllPlayCountAggregationTypes] = useState<PlayCountAggregationTypes>([]);

    const [vendorShareStrategyCheckboxDisabledStatus, setVendorShareStrategyCheckboxDisabledStatus] = useState<
        Record<string, boolean>
    >({});

    const vendorShareCalculationTypesInOrder = Object.keys(VendorShareCalculationTypesEnum);
    const playCountAggregationTypesInOrder = Object.keys(PlayCountAggregationTypesEnum);

    const selectedVendorShareCalculationTypeIds =
        contractTerms.vendorShareCalculationMapping?.map(
            (vendorShareCalculationItem) =>
                vendorShareCalculationItem.vendorShareCalculationType?.vendorShareCalculationTypeId ?? -1
        ) ?? [];

    const eligibleForSubmissionVendorShareCalculationType =
        contractTerms.vendorShareCalculationMapping?.find(
            (vendorShareCalculationItem) => vendorShareCalculationItem.eligibleForEarningsSubmission === true
        ) ?? {};

    const selectedPlayCountAggregationTypeIds =
        contractTerms.playCountAggregationTypeMapping?.map(
            (playCountAggregationItem) =>
                playCountAggregationItem.playCountAggregationType?.playCountAggregationTypeId ?? -1
        ) ?? [];

    const eligibleForSubmissionPlayCountAggregationType =
        contractTerms.playCountAggregationTypeMapping?.find(
            (playCountAggregationItem) => playCountAggregationItem.eligibleForEarningsSubmission === true
        ) ?? {};

    const containsStrategyType = (strategyTypeList: string[] | number[], strategyType: string | number): boolean => {
        return strategyTypeList.some((strategyTypeListItem) => strategyTypeListItem === strategyType);
    };

    const addPlayCountStrategy = (playCountStrategyToAdd: PlayCountAggregationType | undefined) => {
        if (isUndefined(playCountStrategyToAdd)) return;

        const playCountStrategyMappingList = cloneDeep(contractTerms.playCountAggregationTypeMapping) ?? [];

        handleContractTermValueChange('playCountAggregationTypeMapping', '', [
            ...playCountStrategyMappingList,
            {
                playCountAggregationType: {
                    playCountAggregationTypeId: playCountStrategyToAdd.playCountAggregationTypeId,
                    playCountAggregationType: playCountStrategyToAdd.playCountAggregationType
                },
                // Similar as vendor share strategy, which relies on the dropdown to determine the eligible one
                eligibleForEarningsSubmission: false
            }
        ]);
    };

    const removePlayCountStrategy = (playCountStrategyNameToRemove: keyof typeof PlayCountAggregationTypesEnum) => {
        const playCountStrategyMappingList = cloneDeep(contractTerms.playCountAggregationTypeMapping) ?? [];

        handleContractTermValueChange(
            'playCountAggregationTypeMapping',
            '',
            playCountStrategyMappingList.filter(
                (strategyType) =>
                    strategyType.playCountAggregationType?.playCountAggregationType !== playCountStrategyNameToRemove
            )
        );
    };

    const updatePlayCountStrategyForSubmission = (
        playCountStrategyNameToBeEligible: keyof typeof PlayCountAggregationTypesEnum
    ) => {
        const playCountStrategyMappingList = cloneDeep(contractTerms.playCountAggregationTypeMapping) ?? [];

        handleContractTermValueChange(
            'playCountAggregationTypeMapping',
            '',
            playCountStrategyMappingList.map((strategyType) => {
                if (
                    strategyType.playCountAggregationType?.playCountAggregationType ===
                    playCountStrategyNameToBeEligible
                ) {
                    return {
                        ...strategyType,
                        eligibleForEarningsSubmission: true
                    };
                } else {
                    return {
                        ...strategyType,
                        eligibleForEarningsSubmission: false
                    };
                }
            })
        );
    };

    // Updates the vendor share and play count strategy based on checkbox selections.
    const handleVendorShareStrategyTypeChange = (
        checkboxStatus: boolean,
        strategyTypeId: number,
        strategyTypeName: string
    ) => {
        const vendorShareStrategyMappingList = cloneDeep(contractTerms.vendorShareCalculationMapping) ?? [];
        const standardPlayCountStrategy = allPlayCountAggregationTypes.find(
            (playCountAggregationTypeItem) =>
                playCountAggregationTypeItem.playCountAggregationType === PlayCountAggregationTypesEnum.STANDARD
        );
        const artistCentricPlayCountStrategy = allPlayCountAggregationTypes.find(
            (playCountAggregationTypeItem) =>
                playCountAggregationTypeItem.playCountAggregationType ===
                PlayCountAggregationTypesEnum.ARTIST_CENTRIC_2025
        );

        // Add a vendor share strategy
        if (checkboxStatus) {
            handleContractTermValueChange('vendorShareCalculationMapping', '', [
                ...vendorShareStrategyMappingList,
                {
                    vendorShareCalculationType: {
                        vendorShareCalculationTypeId: strategyTypeId,
                        vendorShareCalculationType: strategyTypeName
                    },
                    // For new added vendor share strategy, default eligibleForEarningsSubmission=false and rely on the dropdown selection to set the eligible one
                    eligibleForEarningsSubmission: false
                }
            ]);

            // Update corresponding play count strategy
            if (
                strategyTypeName === VendorShareCalculationTypesEnum.STANDARD ||
                strategyTypeName === VendorShareCalculationTypesEnum.ANTI_DILUTION
            ) {
                addPlayCountStrategy(standardPlayCountStrategy);
            } else if (
                strategyTypeName === VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_STANDARD ||
                strategyTypeName === VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_ANTI_DILUTION
            ) {
                addPlayCountStrategy(artistCentricPlayCountStrategy);
            }
        }
        // Remove a vendor share strategy
        else {
            handleContractTermValueChange(
                'vendorShareCalculationMapping',
                '',
                vendorShareStrategyMappingList.filter(
                    (strategyType) =>
                        strategyType.vendorShareCalculationType?.vendorShareCalculationTypeId !== strategyTypeId
                )
            );

            // Remove corresponding play count strategy
            if (
                strategyTypeName === VendorShareCalculationTypesEnum.STANDARD ||
                strategyTypeName === VendorShareCalculationTypesEnum.ANTI_DILUTION
            ) {
                removePlayCountStrategy(PlayCountAggregationTypesEnum.STANDARD);
            } else if (
                strategyTypeName === VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_STANDARD ||
                strategyTypeName === VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_ANTI_DILUTION
            ) {
                removePlayCountStrategy(PlayCountAggregationTypesEnum.ARTIST_CENTRIC_2025);
            }
        }
    };

    // Updates the vendor share and play count strategy that are eligible for earnings submission based on dropdown selection.
    const handleVendorShareStrategyForSubmissionChange = (strategyTypeName: string) => {
        const vendorShareStrategyMappingList = cloneDeep(contractTerms.vendorShareCalculationMapping) ?? [];

        handleContractTermValueChange(
            'vendorShareCalculationMapping',
            '',
            vendorShareStrategyMappingList.map((strategyType) => {
                if (strategyType.vendorShareCalculationType?.vendorShareCalculationType === strategyTypeName) {
                    return {
                        ...strategyType,
                        eligibleForEarningsSubmission: true
                    };
                } else {
                    return {
                        ...strategyType,
                        eligibleForEarningsSubmission: false
                    };
                }
            })
        );

        // Update corresponding play count strategy that is eligible for earnings submission
        if (
            strategyTypeName === VendorShareCalculationTypesEnum.STANDARD ||
            strategyTypeName === VendorShareCalculationTypesEnum.ANTI_DILUTION
        ) {
            updatePlayCountStrategyForSubmission(PlayCountAggregationTypesEnum.STANDARD);
        } else if (
            strategyTypeName === VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_STANDARD ||
            strategyTypeName === VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_ANTI_DILUTION
        ) {
            updatePlayCountStrategyForSubmission(PlayCountAggregationTypesEnum.ARTIST_CENTRIC_2025);
        }
    };

    // When vendor share strategy checkbox and dropdown values change,
    // update which checkboxes would be disabled based on business rules, and trigger validation error when necessary
    useEffect(() => {
        if (isEmpty(contractTerms.vendorShareCalculationMapping)) {
            setVendorShareStrategyCheckboxDisabledStatus({
                [VendorShareCalculationTypesEnum.STANDARD]: false,
                [VendorShareCalculationTypesEnum.ANTI_DILUTION]: false,
                [VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_STANDARD]: false,
                [VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_ANTI_DILUTION]: false
            });

            // Display error message for both checkbox section and dropdown
            // when there is no selection for any vendor share strategy checkbox
            if (handleContractTermErrorMessageChange) {
                handleContractTermErrorMessageChange(
                    'vendorSharePlayCountCalcStrategy',
                    'vendorShareStrategy',
                    MUST_N_SELECTIONS(1)
                );
                handleContractTermErrorMessageChange(
                    'vendorSharePlayCountCalcStrategy',
                    'vendorShareStrategyForEarningSubmission',
                    IS_REQUIRED
                );
            }
        } else {
            // Display error message when there is no selection in dropdown
            if (isEmpty(eligibleForSubmissionVendorShareCalculationType) && handleContractTermErrorMessageChange) {
                handleContractTermErrorMessageChange &&
                    handleContractTermErrorMessageChange(
                        'vendorSharePlayCountCalcStrategy',
                        'vendorShareStrategyForEarningSubmission',
                        IS_REQUIRED
                    );
            }

            if (handleContractTermErrorMessageChange) {
                handleContractTermErrorMessageChange('vendorSharePlayCountCalcStrategy', 'vendorShareStrategy', '');
            }

            // Disable & Enable vendor share strategy checkboxes based on business rules
            (contractTerms.vendorShareCalculationMapping ?? []).forEach((vendorShareCalculationItem) => {
                if (
                    vendorShareCalculationItem.vendorShareCalculationType?.vendorShareCalculationType ===
                        VendorShareCalculationTypesEnum.STANDARD ||
                    vendorShareCalculationItem.vendorShareCalculationType?.vendorShareCalculationType ===
                        VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_STANDARD
                ) {
                    setVendorShareStrategyCheckboxDisabledStatus({
                        [VendorShareCalculationTypesEnum.STANDARD]: false,
                        [VendorShareCalculationTypesEnum.ANTI_DILUTION]: true,
                        [VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_STANDARD]: false,
                        [VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_ANTI_DILUTION]: true
                    });
                } else if (
                    vendorShareCalculationItem.vendorShareCalculationType?.vendorShareCalculationType ===
                        VendorShareCalculationTypesEnum.ANTI_DILUTION ||
                    vendorShareCalculationItem.vendorShareCalculationType?.vendorShareCalculationType ===
                        VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_ANTI_DILUTION
                ) {
                    setVendorShareStrategyCheckboxDisabledStatus({
                        [VendorShareCalculationTypesEnum.STANDARD]: true,
                        [VendorShareCalculationTypesEnum.ANTI_DILUTION]: false,
                        [VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_STANDARD]: true,
                        [VendorShareCalculationTypesEnum.ARTIST_CENTRIC_2025_ANTI_DILUTION]: false
                    });
                }
            });
        }
    }, [contractTerms.vendorShareCalculationMapping]);

    useEffect(() => {
        const vendorShareCalculationTypesPromise = contractConfigurationClient
            .getVendorShareCalculationTypes(GetVendorShareCalculationTypesInput.clone({}))
            .toPromise();

        const playCountAggregationTypesPromise = contractConfigurationClient
            .getPlayCountAggregationTypes(GetPlayCountAggregationTypesInput.clone({}))
            .toPromise();

        Promise.all([vendorShareCalculationTypesPromise, playCountAggregationTypesPromise])
            .then((response) => {
                const [vendorShareCalculationTypesResponse, playCountAggregationTypesResponse] = response;
                setAllVendorShareCalculationTypes(
                    sortBy(
                        vendorShareCalculationTypesResponse.vendorShareCalculationTypes ?? [],
                        (type: VendorShareCalculationType) =>
                            indexOf(vendorShareCalculationTypesInOrder, type.vendorShareCalculationType)
                    )
                );
                setAllPlayCountAggregationTypes(
                    sortBy(
                        playCountAggregationTypesResponse.playCountAggregationTypes ?? [],
                        (type: PlayCountAggregationType) =>
                            indexOf(playCountAggregationTypesInOrder, type.playCountAggregationType)
                    )
                );
            })
            .catch((error: UnknownCoralError) => {
                // eslint-disable-next-line no-console
                console.error(
                    'GetVendorShareCalculationTypes and GetPlayCountAggregationTypes APIs Unexpected error',
                    error
                );
                setTopAlertMsg(error.receivedException.message);
            });
    }, []);

    const handleVendorShareStrategyForSubmissionErrorMsgChange = () => {
        if (!handleContractTermErrorMessageChange) return;
        handleContractTermErrorMessageChange(
            'vendorSharePlayCountCalcStrategy',
            'vendorShareStrategyForEarningSubmission',
            isEmpty(eligibleForSubmissionVendorShareCalculationType) ? IS_REQUIRED : ''
        );
    };

    useEffect(() => {
        if (!setIsContractTermInputValid) return;
        setIsContractTermInputValid((prevState) => ({
            ...prevState,
            vendorSharePlayCountCalcStrategy: !hasErrorMessage(
                contractTermErrorMessages!.vendorSharePlayCountCalcStrategy
            )
        }));
    }, [contractTermErrorMessages!.vendorSharePlayCountCalcStrategy]);

    return (
        <>
            <Box className="box-border-light-gray" type="outline" spacingInset="400">
                <Column>
                    <Row spacing="100">
                        <Heading level={3}>Vendor share and play count strategy</Heading>
                        <ContractTermInfoButton contractTermName={ContractTermName.VendorSharePlayCountCalcStrategy} />
                    </Row>
                    <Column>
                        <Heading level={4}>Vendor share strategy</Heading>
                        {isEditMode &&
                            !isEmpty(
                                contractTermErrorMessages!.vendorSharePlayCountCalcStrategy.vendorShareStrategy
                            ) && (
                                <Alert type="error" size="small">
                                    {contractTermErrorMessages!.vendorSharePlayCountCalcStrategy.vendorShareStrategy}
                                </Alert>
                            )}
                        {allVendorShareCalculationTypes.map((vendorShareCalculationType) =>
                            isEditMode ? (
                                <Checkbox
                                    key={vendorShareCalculationType.vendorShareCalculationTypeId!}
                                    data-testid={vendorShareCalculationType.vendorShareCalculationType}
                                    checked={containsStrategyType(
                                        selectedVendorShareCalculationTypeIds,
                                        vendorShareCalculationType.vendorShareCalculationTypeId!
                                    )}
                                    onChange={(val) =>
                                        handleVendorShareStrategyTypeChange(
                                            val,
                                            vendorShareCalculationType.vendorShareCalculationTypeId!,
                                            vendorShareCalculationType.vendorShareCalculationType!
                                        )
                                    }
                                    disabled={
                                        vendorShareStrategyCheckboxDisabledStatus[
                                            vendorShareCalculationType.vendorShareCalculationType!
                                        ]
                                    }
                                >
                                    {getStrategyTypeDisplayName(vendorShareCalculationType.vendorShareCalculationType!)}
                                </Checkbox>
                            ) : (
                                <Toggle
                                    key={vendorShareCalculationType.vendorShareCalculationTypeId}
                                    data-testid={vendorShareCalculationType.vendorShareCalculationType}
                                    label={getStrategyTypeDisplayName(
                                        vendorShareCalculationType.vendorShareCalculationType!
                                    )}
                                    value={containsStrategyType(
                                        selectedVendorShareCalculationTypeIds,
                                        vendorShareCalculationType.vendorShareCalculationTypeId!
                                    )}
                                    isEditMode={isEditMode}
                                />
                            )
                        )}
                        {isEditMode ? (
                            <Select
                                label="Select vendor share strategy eligible for earnings submission"
                                value={
                                    eligibleForSubmissionVendorShareCalculationType.vendorShareCalculationType
                                        ?.vendorShareCalculationType
                                }
                                onChange={(val) => handleVendorShareStrategyForSubmissionChange(val)}
                                size="xlarge"
                                onBlur={handleVendorShareStrategyForSubmissionErrorMsgChange}
                                errorMessage={
                                    contractTermErrorMessages!.vendorSharePlayCountCalcStrategy
                                        .vendorShareStrategyForEarningSubmission
                                }
                                error={
                                    !isEmpty(
                                        contractTermErrorMessages!.vendorSharePlayCountCalcStrategy
                                            .vendorShareStrategyForEarningSubmission
                                    )
                                }
                            >
                                {allVendorShareCalculationTypes.map((vendorShareCalculationType) => (
                                    <SelectOption
                                        key={vendorShareCalculationType.vendorShareCalculationTypeId}
                                        value={vendorShareCalculationType.vendorShareCalculationType!}
                                        label={getStrategyTypeDisplayName(
                                            vendorShareCalculationType.vendorShareCalculationType!
                                        )}
                                        disabled={
                                            !containsStrategyType(
                                                selectedVendorShareCalculationTypeIds,
                                                vendorShareCalculationType.vendorShareCalculationTypeId!
                                            )
                                        }
                                    />
                                ))}
                            </Select>
                        ) : (
                            <Input
                                label="Vendor share strategy eligible for earnings submission"
                                value={getStrategyTypeDisplayName(
                                    eligibleForSubmissionVendorShareCalculationType.vendorShareCalculationType
                                        ?.vendorShareCalculationType ?? ''
                                )}
                                isEditMode={isEditMode}
                                readModeLabelType="h100"
                            />
                        )}
                    </Column>
                    <Column>
                        <Heading level={4}>Play count strategy</Heading>
                        {isEditMode && (
                            <Text type="b200" color="secondary">
                                Selections are automatically set based on Vendor Share Strategy choices
                            </Text>
                        )}
                        {allPlayCountAggregationTypes.map((playCountAggregationType) =>
                            isEditMode ? (
                                <Checkbox
                                    key={playCountAggregationType.playCountAggregationTypeId}
                                    data-testid={playCountAggregationType.playCountAggregationType}
                                    checked={containsStrategyType(
                                        selectedPlayCountAggregationTypeIds,
                                        playCountAggregationType.playCountAggregationTypeId!
                                    )}
                                    disabled={true}
                                >
                                    {getStrategyTypeDisplayName(playCountAggregationType.playCountAggregationType!)}
                                </Checkbox>
                            ) : (
                                <Toggle
                                    key={playCountAggregationType.playCountAggregationTypeId}
                                    data-testid={playCountAggregationType.playCountAggregationType}
                                    label={getStrategyTypeDisplayName(
                                        playCountAggregationType.playCountAggregationType!
                                    )}
                                    value={containsStrategyType(
                                        selectedPlayCountAggregationTypeIds,
                                        playCountAggregationType.playCountAggregationTypeId!
                                    )}
                                    isEditMode={isEditMode}
                                />
                            )
                        )}
                        {isEditMode ? (
                            <Select
                                label="Select play count strategy eligible for earnings submission"
                                value={
                                    eligibleForSubmissionPlayCountAggregationType.playCountAggregationType
                                        ?.playCountAggregationTypeId
                                }
                                size="xlarge"
                                disabled={true}
                            >
                                {allPlayCountAggregationTypes.map((playCountAggregationType) => (
                                    <SelectOption
                                        key={playCountAggregationType.playCountAggregationTypeId}
                                        value={playCountAggregationType.playCountAggregationTypeId!}
                                        label={getStrategyTypeDisplayName(
                                            playCountAggregationType.playCountAggregationType!
                                        )}
                                    />
                                ))}
                            </Select>
                        ) : (
                            <Input
                                label="Play count strategy eligible for earnings submission"
                                value={getStrategyTypeDisplayName(
                                    eligibleForSubmissionPlayCountAggregationType.playCountAggregationType
                                        ?.playCountAggregationType ?? ''
                                )}
                                isEditMode={isEditMode}
                                readModeLabelType="h100"
                            />
                        )}
                    </Column>
                </Column>
            </Box>
        </>
    );
};
